import {defineStore} from 'pinia'
import {ref} from 'vue'
import {useRouter} from 'vue-router'

import {LoginService, UserService} from '@/services/admin'
import type {ILoginData, IUser} from '@/types/shared'

export const useUserStore = defineStore(
  'user-store',
  () => {
    const router = useRouter()
    const isAuthenticated = ref(false)
    const user = ref<IUser | null>(null)

    async function signIn(userData: ILoginData): Promise<void> {
      try {
        const res = await LoginService.login(userData)
        if (res && res.access_token && res.user && res.user.attivo) {
          localStorage.setItem('access_token', res.access_token)
          user.value = res.user as IUser
          isAuthenticated.value = true

          // Redirect to the intended page or default page
          await router.push({ name: 'dashboard' })
        } else {
          throw new Error('Login failed, no token received')
        }
      } catch (err) {
        console.error('Sign in error:', err)
        isAuthenticated.value = false
        user.value = null
        throw err
      }
    }

    async function signOut(redirect: boolean = false) {
      if (user.value) {
        isAuthenticated.value = false
        user.value = null
        localStorage.clear()
      }

      if (redirect) {
        await router.push({ name: 'login' })
      }
    }

    async function updateUserInfo(id_user: number): Promise<void> {
      try {
        const res = await UserService.getOne(id_user)
        if (res) {
          user.value = res as IUser
        } else {
          throw new Error('Failed to update user info')
        }
      } catch (err) {
        console.error('Update user info error:', err)
        throw err
      }
    }

    return { user, isAuthenticated, signIn, signOut, updateUserInfo }
  },
  {
    persist: {
      key: 'user-store',
      serializer: {
        serialize: (state) => {
          const stateCopy = JSON.parse(JSON.stringify(state))
          if (stateCopy.user) {
            delete stateCopy.user.password
          }
          return JSON.stringify(stateCopy)
        },
        deserialize: (str) => JSON.parse(str),
      },
    },
  },
)
